import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { useImmer } from "use-immer";
import ImageDesign from "./ImageDesign";
import VipInfos from "./VipInfos";
import MyDesignInfos from "./MyDesignInfos";
import ClientInfos from "./ClientInfos";
import References from "./References";
import { useTranslation } from "react-i18next";
import SaveCrmData from "./SaveCrmData";
import { crmService } from "../../services/crm.service";
import { useSnackbar } from "notistack";
import { nationalites } from "../../assets/nationalities";
import { useSelector } from "react-redux";

const PREFIX = "Crm";

const classes = {
  root: `${PREFIX}-root`,
  firstAccordionStyle: `${PREFIX}-firstAccordionStyle`,
  accordionStyle: `${PREFIX}-accordionStyle`,
  lastAccordionStyle: `${PREFIX}-lastAccordionStyle`,
  textAndIconStyle: `${PREFIX}-textAndIconStyle`,
  createCrmContainer: `${PREFIX}-createCrmContainer`,
  noCrmTitle: `${PREFIX}-noCrmTitle`,
  noTargetTitle: `${PREFIX}-noTargetTitle`,
};

const StyledDiv = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      marginTop: "3rem",
      width: "100%",
      padding: "0 0.5rem",
      [theme.breakpoints.down("sm")]: {
        marginBottom: "5rem",
      },
    },
    [`& .${classes.firstAccordionStyle}`]: {
      width: "100%",
      maxWidth: "650px",
      marginTop: "2rem",
      backgroundColor: "#303030",
    },
    [`& .${classes.accordionStyle}`]: {
      width: "100%",
      maxWidth: "650px",
      backgroundColor: "#303030",
      "&:last-of-type": {
        marginBottom: "2rem",
      },
    },
    [`& .${classes.textAndIconStyle}`]: {
      color: "#D3AF5F",
    },
  };
});

const NoCrmContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.noCrmTitle}`]: {
      width: "100%",
      textAlign: "center",
      margin: "5rem 0 1rem 0",
    },
    [`& .${classes.createCrmContainer}`]: {
      display: "flex",
      justifyContent: "center",
      width: "100%",
    },
  };
});

const NoTargetContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.noTargetTitle}`]: {
      width: "100%",
      textAlign: "center",
      margin: "5rem 0 1rem 0",
    },
  };
});

function dataDefault(imgUrl) {
  return {
    vip: {
      firstname: "",
      lastname: "",
      nationality: [],
      birthDate: "",
    },
    myDesign: {
      type: "prospect",
      // transactionType: "ar",
      transactionFrame: true,
      transactionAR: false,
      transactionPoster: false,
      men: false,
      women: false,
      other: false,
      imgUrl: imgUrl ? imgUrl : "",
      soldFrames: [
        // {
        // quantity: "",
        // size: "",
        // format: "",
        // }
      ],
      frameType: [],
      activity: [],
      club: [],
      soldDate: "",
    },
    buyer: {
      managers: [],
      managerDetails: [],
      buyerType: "",
      firstname: "",
      lastname: "",
      story: "",
      history: [],
    },
    references: [],
  };
}

function CrmContainer({ selectedTarget, setSelectedTarget, imgUrl }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useImmer(dataDefault(imgUrl));
  const [isLoading, setIsLoading] = useState(false);
  const [crmExist, setCrmExist] = useState(false);

  const isBizDev = useSelector((state) =>
    state.user?.currentUser?.account?.rights.includes("bizDev")
  );
  const isSuperAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.isSuperAdmin === 1
  );

  const getNationalites = React.useCallback((data) => {
    return data.map((nat) =>
      nationalites.find((natRef) => natRef.cca2 === nat)
    );
  }, []);

  console.log("data", data);

  useEffect(() => {
    if (selectedTarget) {
      setIsLoading(true);
      crmService
        .getCrm(selectedTarget.frameId, selectedTarget.id)
        .then((res) => {
          setData((draft) => {
            if (res._id) {
              draft._id = res._id;
              draft.references = res.references;
            }
            draft.vip = {
              ...res.vip,
              nationality: res.vip.nationality
                ? getNationalites(res.vip.nationality)
                : [],
            };
            draft.myDesign = {
              ...res.myDesign,
              frameType: res.myDesign.frameType || [],
              activity: res.myDesign.activity || [],
              club: res.myDesign.club || [],
            };
            draft.myDesign.imgUrl = imgUrl;
            draft.myDesign.soldFrames = res.myDesign.soldFrames || [];
            draft.buyer = {
              ...res.buyer,
              managers: res.buyer.managers || [],
              managerDetails: res.buyer.managerDetails || [],
            };
            draft.references = res.references || [];
          });
          setIsLoading(false);
          setCrmExist(true);
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          setData(dataDefault());
          setIsLoading(false);
          setCrmExist(false);
        });
    }
  }, [selectedTarget]);

  function updateData(targetCategory, key, value) {
    setData((data) => {
      data[targetCategory][key] = value;
    });
  }

  const canUserModifyData = React.useMemo(() => {
    return isSuperAdmin || (!isSuperAdmin && !isBizDev);
  }, [isBizDev, isSuperAdmin]);

  if (isLoading) {
    return (
      <div className="loading-container">
        <CircularProgress size={50} />
        <Typography>{t("loaders.fetching")}</Typography>
      </div>
    );
  } else if (!selectedTarget) {
    return (
      <NoTargetContainer>
        <Typography variant="body2" className={classes.noTargetTitle}>
          {t("Crm.noTarget")}
        </Typography>
      </NoTargetContainer>
    );
  } else if (!isLoading && !crmExist) {
    return (
      <NoCrmContainer>
        <Typography variant="h5" className={classes.noCrmTitle}>
          {t("Crm.noCrm")}
        </Typography>
        <div className={classes.createCrmContainer}>
          {canUserModifyData && (
            <Button
              variant="contained"
              endIcon={<AddCircleIcon />}
              disabled={!canUserModifyData}
              onClick={() => setCrmExist(true)}
            >
              {t("Crm.createCrm")}
            </Button>
          )}
        </div>
      </NoCrmContainer>
    );
  } else
    return (
      <StyledDiv>
        <div className={classes.root}>
          <ImageDesign
            imageUrl={data.myDesign.imgUrl}
            updateData={updateData}
            canUserModifyData={canUserModifyData}
          />
          <Accordion className={classes.firstAccordionStyle} expanded>
            <AccordionSummary
              expandIcon={<ExpandMore className={classes.textAndIconStyle} />}
              aria-controls="vip-infos-content"
              id="vip-infos-header"
            >
              <Typography className={classes.textAndIconStyle}>
                {t("Crm.vipInfosTitle")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <VipInfos
                updateData={updateData}
                data={data}
                canUserModifyData={canUserModifyData}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion className={classes.accordionStyle} expanded>
            <AccordionSummary
              expandIcon={<ExpandMore className={classes.textAndIconStyle} />}
              aria-controls="myDesign-infos-content"
              id="myDesign-infos-header"
            >
              <Typography className={classes.textAndIconStyle}>
                {t("Crm.myDesignInfosTitle")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <MyDesignInfos
                updateData={updateData}
                setData={setData}
                data={data}
                canUserModifyData={canUserModifyData}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion className={classes.accordionStyle} expanded>
            <AccordionSummary
              expandIcon={<ExpandMore className={classes.textAndIconStyle} />}
              aria-controls="client-infos-content"
              id="client-infos-header"
            >
              <Typography className={classes.textAndIconStyle}>
                {t("Crm.clientInfosTitle")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ClientInfos
                updateData={updateData}
                data={data}
                canUserModifyData={canUserModifyData}
              />
            </AccordionDetails>
          </Accordion>
          {data._id && (
            <Accordion className={classes.accordionStyle}>
              <AccordionSummary
                expandIcon={<ExpandMore className={classes.textAndIconStyle} />}
                aria-controls="references-content"
                id="references-header"
              >
                <Typography className={classes.textAndIconStyle}>
                  {t("Crm.referencesTitle")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <References
                  data={data}
                  setData={setData}
                  canUserModifyData={canUserModifyData}
                />
              </AccordionDetails>
            </Accordion>
          )}
        </div>
        {canUserModifyData && (
          <SaveCrmData
            data={data}
            setData={setData}
            selectedTarget={selectedTarget}
            setSelectedTarget={setSelectedTarget}
          />
        )}
      </StyledDiv>
    );
}

export default CrmContainer;
